import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Globe2, TrendingUp, TrendingDown, Minus, Users, HelpCircle } from "lucide-react";
import client from "@/api/client";

const RANGES = [7, 30, 90];

function Stat({ icon: Icon, label, value, testId }) {
  return (
    <Card className="rounded-2xl border border-border p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        <span className="text-[10px] uppercase tracking-widest">{label}</span>
      </div>
      <p className="font-display text-2xl font-semibold mt-1.5 tabular-nums">{value}</p>
    </Card>
  );
}

function TrendPill({ trend, pct }) {
  const Icon = trend === "up" ? TrendingUp : trend === "down" ? TrendingDown : Minus;
  const cls = trend === "up"
    ? "text-emerald-600 bg-emerald-500/10 border-emerald-500/25"
    : trend === "down"
      ? "text-rose-600 bg-rose-500/10 border-rose-500/25"
      : "text-muted-foreground bg-muted border-border";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${cls}`}>
      <Icon className="h-3 w-3" />
      {trend === "flat" ? "—" : `${pct > 0 ? "+" : ""}${pct}%`}
    </span>
  );
}

export function AdminCountryInsights() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get("/admin/insights/countries", { params: { days } });
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  const max = Math.max(1, ...items.map((i) => i.members));

  return (
    <div className="space-y-5" data-testid="admin-country-insights-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
            {t("countryInsights.title", { defaultValue: "Countries" })}
          </h1>
          <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl">
            {t("countryInsights.subtitle", { defaultValue: "Where your members are joining from, and which markets are growing." })}
          </p>
        </div>
        <div className="flex gap-1.5">
          {RANGES.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                days === d ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-accent"
              }`}
              data-testid={`country-insights-range-${d}`}
            >
              {t("countryInsights.lastDays", { count: d, defaultValue: `${d} days` })}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid gap-3 sm:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>
      ) : !data ? (
        <Card className="rounded-2xl border border-dashed p-8 text-center text-sm text-muted-foreground">
          {t("common.error")}
        </Card>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat icon={Users} label={t("countryInsights.members", { defaultValue: "Members" })} value={data.totals.members} testId="country-insights-total" />
            <Stat icon={Globe2} label={t("countryInsights.countries", { defaultValue: "Countries" })} value={data.totals.countries} testId="country-insights-countries" />
            <Stat icon={TrendingUp} label={t("countryInsights.withCountry", { defaultValue: "With a country" })} value={data.totals.with_country} testId="country-insights-known" />
            <Stat icon={HelpCircle} label={t("countryInsights.unknown", { defaultValue: "Not set yet" })} value={data.totals.unknown} testId="country-insights-unknown" />
          </div>

          {(data.rising || []).length > 0 && (
            <Card className="rounded-2xl border border-border p-4 sm:p-5" data-testid="country-insights-rising">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                {t("countryInsights.rising", { defaultValue: "Growing markets" })}
              </p>
              <div className="mt-2.5 flex flex-wrap gap-2">
                {data.rising.map((r) => (
                  <span key={r.country} className="inline-flex items-center gap-2 rounded-full border border-primary/25 bg-primary/5 px-3 py-1.5" data-testid={`country-insights-rising-${r.country}`}>
                    <span className="text-sm font-medium">{r.country}</span>
                    <span className="text-xs text-muted-foreground">+{r.new_in_period}</span>
                    <TrendPill trend={r.trend} pct={r.growth_pct} />
                  </span>
                ))}
              </div>
            </Card>
          )}

          <Card className="rounded-2xl border border-border overflow-hidden">
            <div className="divide-y divide-border" data-testid="country-insights-list">
              {items.length === 0 && (
                <p className="p-8 text-center text-sm text-muted-foreground" data-testid="country-insights-empty">
                  {t("countryInsights.empty", { defaultValue: "No members yet." })}
                </p>
              )}
              {items.map((i) => (
                <div key={i.country} className="p-3.5 sm:px-5" data-testid={`country-insights-row-${i.country}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => navigate(`/admin?section=users&country=${encodeURIComponent(i.country)}`)}
                      className="text-sm font-semibold min-w-0 flex-1 truncate text-start hover:text-primary hover:underline"
                      title={t("countryInsights.viewMembers", { defaultValue: "View members from here" })}
                      data-testid={`country-insights-open-${i.country}`}
                    >
                      {i.country}
                    </button>
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {i.new_in_period} {t("countryInsights.newShort", { defaultValue: "new" })}
                    </span>
                    <TrendPill trend={i.trend} pct={i.growth_pct} />
                    <span className="text-sm font-semibold tabular-nums w-8 text-end">{i.members}</span>
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
                    <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${Math.round((i.members / max) * 100)}%` }} />
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {Object.entries(i.roles || {}).filter(([, n]) => n > 0).map(([role, n]) => (
                      <Badge key={role} variant="outline" className="rounded-full text-[10px]">
                        {t(`roles.${role}`, { defaultValue: role })} · {n}
                      </Badge>
                    ))}
                    {i.pending > 0 && (
                      <Badge className="rounded-full bg-amber-500/12 text-amber-700 border-amber-500/30 text-[10px]">
                        {i.pending} {t("usersManager.statusPending", { defaultValue: "pending" })}
                      </Badge>
                    )}
                    {i.visits > 0 && (
                      <Badge variant="outline" className="rounded-full text-[10px]">
                        {i.visits} {t("countryInsights.visits", { defaultValue: "visits" })}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

export default AdminCountryInsights;
